'use client';

import { useCallback, useEffect, useId, useState } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { PLANNER_HREF } from '../brand';
import { formatGbp } from '../data/doorPricing';
import { useSpatialPorts } from '../data/ports';
import { priceRequestState, type PriceRequest } from '../data/priceRequests';
import { SignInRequired, type SavedDesign, type SavedDesignKind } from '../data/savedDesigns';
import { adoptBasket } from '../shared/basket';
import { headingLevel, text, type Localized } from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * "My designs" (plan K6): everything a shopper has saved, in one place,
 * filtered by type. Each card opens in its own tool.
 *
 * <p>Reads the `savedDesigns` port: the shopper's account on the server. A
 * shopper who is not signed in is sent to sign in and brought back here.
 *
 * <p>Above the designs, the shopper's price requests (made-to-measure goods
 * the shop prices by hand): "Price requested" until the shop sends a quote,
 * then "Quoted £x, valid until …" with Accept, which turns the quote into
 * their basket. There is no mail on this estate; this is where the answer is.
 *
 * <p>Thumbnails are shown as `<img>` data URIs, never inserted as markup: a
 * saved design comes back from storage, and an image cannot run script.
 */
export interface SpatialKitchenMyDesignsProps {
  title?: Localized;
  intro?: Localized;
  headingLevel?: 'h1' | 'h2';
  plannerHref?: string;
  kitchenHref?: string;
  doorsHref?: string;
  cabinetsHref?: string;
  signInHref?: string;
  /** The basket page an accepted quote opens. */
  basketHref?: string;
}

const KINDS: ReadonlyArray<[SavedDesignKind | 'all', string]> = [
  ['all', 'All'],
  ['kitchen', 'Kitchens'],
  ['bedroom', 'Bedrooms'],
  ['doors', 'Doors'],
  ['cabinets', 'Cabinets'],
  ['bespoke', 'Made-to-match requests'],
];

const KIND_LABEL: Record<SavedDesignKind, string> = {
  kitchen: 'Kitchen',
  bedroom: 'Bedroom',
  doors: 'Door order',
  cabinets: 'Cabinets',
  bespoke: 'Made-to-match request',
};

const STATUS_LABEL: Record<NonNullable<SavedDesign['status']>, string> = {
  draft: 'Draft',
  requested: 'Quote requested',
  quoted: 'Quote ready',
  accepted: 'Accepted',
};

const CSS = `
.sk-md{padding-block:1.5rem 4rem}
.sk-md__wrap{width:100%;max-width:var(--sk-max);margin-inline:auto;padding-inline:var(--sk-gutter)}
.sk-md__title{font-size:clamp(2rem,1.4rem + 2.2vw,3rem)}
.sk-md__head .sk-lead{margin-top:.75rem;max-width:60ch}
.sk-md__notice{margin:1.25rem 0 0;padding:.75rem 1rem;border-radius:var(--sk-radius-sm);background:var(--sk-brass-soft);font-size:.88rem}
.sk-md__bar{display:flex;flex-wrap:wrap;gap:1rem;justify-content:space-between;align-items:center;margin:1.75rem 0 1.25rem}
.sk-md__seg{display:inline-flex;flex-wrap:wrap;background:var(--sk-stone);border-radius:999px;padding:4px;gap:2px}
.sk-md__seg button{border:0;background:transparent;padding:8px 14px;border-radius:999px;font:inherit;font-weight:700;font-size:.86rem;cursor:pointer;color:var(--sk-muted)}
.sk-md__seg button[aria-pressed="true"]{background:var(--sk-white);color:var(--sk-ink);box-shadow:var(--sk-shadow)}
.sk-md__grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:1.25rem;margin:0;padding:0;list-style:none}
.sk-md__card{display:grid;grid-template-rows:auto 1fr;background:var(--sk-white);border:1px solid var(--sk-border);border-radius:var(--sk-radius-card);overflow:hidden}
.sk-md__pic{display:grid;place-items:center;aspect-ratio:4/3;background:var(--sk-warm);padding:1rem}
.sk-md__pic img{max-width:100%;max-height:100%;object-fit:contain}
.sk-md__pic span{font-family:var(--sk-serif);font-size:1.4rem;color:var(--sk-faint)}
.sk-md__body{display:grid;gap:.35rem;align-content:start;padding:1rem 1.1rem 1.1rem}
.sk-md__kind{font-size:.72rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--sk-brass-text)}
.sk-md__body h2{font-family:var(--sk-sans);font-size:1.02rem;font-weight:700;margin:0}
.sk-md__meta{font-size:.84rem;color:var(--sk-faint)}
.sk-md__status{justify-self:start;border-radius:999px;padding:2px 10px;font-size:.75rem;font-weight:800;background:var(--sk-sage-grey);color:var(--sk-sage-deep)}
.sk-md__actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:.6rem;align-items:center}
.sk-md__remove{border:0;background:transparent;font:inherit;font-size:.85rem;color:var(--sk-faint);cursor:pointer;text-decoration:underline}
.sk-md__remove[data-confirm="true"]{color:var(--sk-clay);font-weight:700}
.sk-md__empty{display:grid;gap:1rem;justify-items:start;padding:2rem;border-radius:var(--sk-radius-card);background:var(--sk-white);border:1px dashed var(--sk-stone-deep)}
.sk-md__empty ul{display:flex;flex-wrap:wrap;gap:10px;margin:0;padding:0;list-style:none}
.sk-md__planner{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:1rem;margin-top:1.5rem;padding:1.25rem 1.5rem;border-radius:var(--sk-radius-card);background:var(--sk-sage-grey)}
.sk-md__planner p{margin:0}
.sk-md__requests{margin-top:1.75rem}
.sk-md__requests h2{font-size:1.35rem;margin:0 0 .75rem}
.sk-md__rlist{display:grid;gap:.75rem;margin:0;padding:0;list-style:none}
.sk-md__req{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:1rem;background:var(--sk-white);border:1px solid var(--sk-border);border-radius:var(--sk-radius-card);padding:.25rem .5rem}
.sk-md__req h3{font-family:var(--sk-sans);font-size:1.02rem;font-weight:700;margin:0}
.sk-md__req .sk-md__actions{margin:0 .6rem .6rem}
.sk-md__req[data-tone="quoted"] .sk-md__status{background:var(--sk-brass-soft);color:var(--sk-ink)}
.sk-md__req[data-tone="closed"] .sk-md__status{background:var(--sk-stone);color:var(--sk-muted)}
`;

/** A saved SVG thumbnail as an image source; anything that is not an SVG document is ignored. */
export function thumbnailSrc(svg: string | undefined): string | undefined {
  if (!svg || !/^\s*<svg[\s>]/.test(svg)) return undefined;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}

export function SpatialKitchenMyDesigns(props: SpatialKitchenMyDesignsProps) {
  const id = useId();
  const { savedDesigns, priceRequests } = useSpatialPorts();
  const [designs, setDesigns] = useState<SavedDesign[] | null>(null);
  const [requests, setRequests] = useState<PriceRequest[] | null>(null);
  const [requestsError, setRequestsError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<string | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<SavedDesignKind | 'all'>('all');
  const [confirming, setConfirming] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const Heading = headingLevel(props.headingLevel, 'h1');
  const plannerHref = props.plannerHref || PLANNER_HREF;
  const signInHref = props.signInHref || '/sign-in';

  const load = useCallback(() => {
    // A settled "not signed in" from either list: send the shopper to sign in and back here.
    const toSignIn = () => {
      const here = `${window.location.pathname}${window.location.search}`;
      const base = signInHref;
      window.location.replace(`${base}${base.includes('?') ? '&' : '?'}next=${encodeURIComponent(here)}`);
    };
    void Promise.allSettled([savedDesigns.list(), priceRequests ? priceRequests.list() : Promise.resolve([] as PriceRequest[])]).then(
      ([saved, asked]) => {
        if ((saved.status === 'rejected' && saved.reason instanceof SignInRequired) || (asked.status === 'rejected' && asked.reason instanceof SignInRequired)) {
          toSignIn();
          return;
        }
        if (saved.status === 'fulfilled') {
          setDesigns([...saved.value].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
          setError(null);
        } else {
          setError('Your designs could not be loaded. Please try again in a moment.');
        }
        if (asked.status === 'fulfilled') {
          setRequests(asked.value);
          setRequestsError(null);
        } else {
          setRequestsError('Your price requests could not be loaded. Please try again in a moment.');
        }
      },
    );
  }, [savedDesigns, priceRequests, signInHref]);

  async function accept(request: PriceRequest) {
    const number = request.quote?.quoteNumber;
    if (!priceRequests || !number) return;
    setAccepting(number);
    setAcceptError(null);
    try {
      const basket = await priceRequests.accept(number);
      if (!adoptBasket(basket)) {
        setAcceptError('Your quote is accepted, but this browser would not open its basket. Please allow site data and reload.');
        load();
        return;
      }
      window.location.assign(props.basketHref || '/cart');
    } catch (e) {
      setAcceptError(e instanceof Error && e.message ? e.message : 'That quote could not be accepted.');
      load();
    } finally {
      setAccepting(null);
    }
  }

  useEffect(load, [load]);

  async function remove(design: SavedDesign) {
    if (confirming !== design.id) {
      setConfirming(design.id);
      return;
    }
    setConfirming(null);
    try {
      await savedDesigns.remove(design.id);
      setStatus(`Removed “${design.title}”.`);
      load();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'That design could not be removed.');
    }
  }

  const shown = (designs ?? []).filter((d) => filter === 'all' || d.kind === filter);
  const counts = new Map<string, number>();
  for (const d of designs ?? []) counts.set(d.kind, (counts.get(d.kind) ?? 0) + 1);

  return (
    <BlockRoot as="section" className="sk sk-md" aria-labelledby={`${id}-title`}>
      <SkBaseStyles />
      <SkStyles id="my-designs" css={CSS} />
      <div className="sk-md__wrap">
        <div className="sk-md__head">
          <Heading id={`${id}-title`} className="sk-heading sk-md__title">
            {text(props.title, 'My designs')}
          </Heading>
          <p className="sk-lead">{text(props.intro, 'Everything you have saved: kitchens, door orders, cabinets and made-to-match requests. Open one to carry on where you left off.')}</p>
        </div>

        {designs === null && !error && <p className="sk-md__meta" style={{ marginTop: '1.5rem' }}>Loading your designs…</p>}
        {error && (
          <p className="sk-md__notice" role="alert">
            {error}
          </p>
        )}

        {requestsError && (
          <p className="sk-md__notice" role="alert">
            {requestsError}
          </p>
        )}
        {requests !== null && requests.length > 0 && (
          <section className="sk-md__requests" aria-labelledby={`${id}-requests`}>
            <h2 id={`${id}-requests`} className="sk-heading">
              Price requests
            </h2>
            {acceptError && (
              <p className="sk-md__notice" role="alert">
                {acceptError}
              </p>
            )}
            <ul className="sk-md__rlist">
              {requests.map((r) => {
                const state = priceRequestState(r);
                const number = r.quote?.quoteNumber;
                const titleId = `${id}-r-${r.id}`;
                return (
                  <li key={r.id} className="sk-md__req" data-tone={state.tone}>
                    <div className="sk-md__body">
                      <h3 id={titleId}>{r.title || 'Price request'}</h3>
                      {r.summary && <span className="sk-md__meta">{r.summary}</span>}
                      <span className="sk-md__meta">
                        {`${r.items.length} item${r.items.length === 1 ? '' : 's'}`}
                        {r.createdAt ? ` · asked ${formatDate(r.createdAt)}` : ''}
                        {number ? ` · quote ${number}` : ''}
                      </span>
                      <span className="sk-md__status">{state.label}</span>
                    </div>
                    <div className="sk-md__actions">
                      {state.canAccept && number && (
                        <button
                          type="button"
                          className="sk-btn sk-btn--primary sk-btn--sm"
                          aria-describedby={titleId}
                          disabled={accepting !== null}
                          onClick={() => void accept(r)}
                        >
                          {accepting === number ? 'Accepting…' : 'Accept and add to basket'}
                        </button>
                      )}
                      {r.quote?.status === 'APPROVED' && (
                        <a className="sk-btn sk-btn--secondary sk-btn--sm" href={props.basketHref || '/cart'} aria-describedby={titleId}>
                          Go to basket
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {designs !== null && designs.length > 0 && (
          <div className="sk-md__bar">
            <div className="sk-md__seg" role="group" aria-label="Show">
              {KINDS.filter(([kind]) => kind === 'all' || counts.has(kind)).map(([kind, label]) => (
                <button key={kind} type="button" aria-pressed={filter === kind} onClick={() => setFilter(kind)}>
                  {kind === 'all' ? `${label} (${designs.length})` : `${label} (${counts.get(kind)})`}
                </button>
              ))}
            </div>
          </div>
        )}
        <p className="sk-visually-hidden" role="status">
          {status ?? ''}
        </p>

        {designs !== null && designs.length === 0 && !requests?.length && (
          <div className="sk-md__empty" style={{ marginTop: '1.5rem' }}>
            <p style={{ margin: 0 }}>Nothing saved yet. Start with one of these:</p>
            <ul>
              <li>
                <a className="sk-btn sk-btn--primary sk-btn--sm" href={props.kitchenHref || PLANNER_HREF}>
                  Design a kitchen
                </a>
              </li>
              <li>
                <a className="sk-btn sk-btn--secondary sk-btn--sm" href={props.doorsHref || '/doors'}>
                  Order doors
                </a>
              </li>
              <li>
                <a className="sk-btn sk-btn--secondary sk-btn--sm" href={props.cabinetsHref || '/cabinets?mode=custom'}>
                  Make a cabinet
                </a>
              </li>
            </ul>
          </div>
        )}

        {shown.length > 0 && (
          <ul className="sk-md__grid">
            {shown.map((d) => {
              const src = thumbnailSrc(d.thumbnailSvg);
              const titleId = `${id}-${d.id}`;
              return (
                <li key={d.id} className="sk-md__card">
                  <div className="sk-md__pic">{src ? <img src={src} alt="" /> : <span aria-hidden="true">{KIND_LABEL[d.kind]}</span>}</div>
                  <div className="sk-md__body">
                    <span className="sk-md__kind">{KIND_LABEL[d.kind]}</span>
                    <h2 id={titleId}>{d.title}</h2>
                    {d.summary && <span className="sk-md__meta">{d.summary}</span>}
                    <span className="sk-md__meta">
                      {`Saved ${formatDate(d.updatedAt)}`}
                      {typeof d.total === 'number' ? ` · ${formatGbp(d.total)} when saved` : ''}
                    </span>
                    {d.status && <span className="sk-md__status">{STATUS_LABEL[d.status]}</span>}
                    <div className="sk-md__actions">
                      <a className="sk-btn sk-btn--primary sk-btn--sm" href={d.href} aria-describedby={titleId}>
                        Open
                      </a>
                      <button type="button" className="sk-md__remove" data-confirm={confirming === d.id} aria-describedby={titleId} onClick={() => remove(d)}>
                        {confirming === d.id ? 'Click again to remove' : 'Remove'}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {designs !== null && designs.length > 0 && (
          <div className="sk-md__planner">
            <p>Start another kitchen in the planner; it is saved here as you go.</p>
            <a className="sk-btn sk-btn--secondary sk-btn--sm" href={props.kitchenHref || PLANNER_HREF}>
              Design a kitchen
            </a>
          </div>
        )}
      </div>
    </BlockRoot>
  );
}
